import { useCreateMediaFolderMutation } from "@api/media-folders";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { type KeyboardEventHandler, useState } from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { track } from "@/features/analytics";
import { useGetCurrentUserQuery } from "@/shared/services";
import { MediaLibDialogBase } from "../../media-lib-dialog-base";

const DIALOG_ID = "media-lib-folder-create";

interface IProps {
  folderParentId?: string;
}

export const MediaLibFolderCreateDialog = ({ folderParentId }: IProps) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState("");
  const { data: currentUser } = useGetCurrentUserQuery();

  const [createFolder, { isLoading }] = useCreateMediaFolderMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (!name?.trim() || isLoading || !currentUser) return;

    try {
      await createFolder({
        title: name.trim(),
        parentId: folderParentId,
        openAccess: true,
        users: null,
      }).unwrap();
    } catch (e) {
      track("error creating new folder", {
        folderParentId,
        folderName: name.trim(),
        error: e,
      });
      toast.error("Error creating new folder");
    }

    handleClose();
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <MediaLibDialogBase>
      <MediaLibDialogBase.Header title="New folder" onClose={handleClose} />
      <MediaLibDialogBase.Content>
        <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px] tw-font-semibold">
          Name
        </span>
        <Input
          placeholder="Add your folder name here..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </MediaLibDialogBase.Content>
      <MediaLibDialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!name?.trim()}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Add folder"
          )}
        </Button>
      </MediaLibDialogBase.Footer>
    </MediaLibDialogBase>
  );
};

MediaLibFolderCreateDialog.dialogId = DIALOG_ID;
